// NOT-542 — the two unit lists every Business Unit picker needs.
//
// A picker answers two DIFFERENT questions, and answering both from a single
// `useBusinessUnits('active')` call is the bug this module exists to prevent:
//
//   • What may be CHOSEN?     → ACTIVE units only. An archived unit must never
//                               become newly assignable.
//   • What IS already chosen? → resolved against EVERY unit, archived included.
//
// Resolving a stored selection against the active list alone silently drops any
// id whose unit has since been archived: the chip disappears from the control
// while the id stays in form state and is still submitted, the API rejects it
// ("Unknown or inactive business unit id(s)"), and the record becomes
// permanently unsavable with nothing on screen to remove. `useBusinessUnitLabel`
// already reads `status: 'all'` for exactly this reason.
//
// Zero-cost-when-unused stays keyed on ACTIVE units: a tenant whose only units
// are archived must still see no control at all, so callers gate on
// `activeUnits.length`, never on the size of the resolution map.
import { useMemo } from 'react';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import type { BusinessUnit } from '@/types/business-unit';

/** Stable empty list so `data === undefined` doesn't churn memo identities. */
const NO_UNITS: BusinessUnit[] = [];

/**
 * A unit is selectable unless it has been archived. Phrased as "not archived"
 * rather than "is ACTIVE" so a unit whose status the caller has not loaded is
 * treated as ordinary rather than quietly hidden.
 */
export function isSelectableBusinessUnit(unit: BusinessUnit): boolean {
  return unit.status !== 'ARCHIVED';
}

/** One entry of a stored selection, resolved against every known unit. */
export interface SelectedBusinessUnit {
  id: string;
  /** The unit itself — undefined when the id resolves to nothing at all. */
  unit: BusinessUnit | undefined;
  /** Assigned but not selectable any more: archived, or gone entirely. */
  archived: boolean;
  /** What to show for it. Never a bare UUID. */
  label: string;
  /** Short marker rendered next to the label; null for a normal unit. */
  note: 'Archived' | 'Unavailable' | null;
}

/**
 * Resolve stored ids to renderable entries, IN SELECTION ORDER (for a job type
 * that order is the preference order and the first unit is the resolver's
 * fallback). Every id produces an entry — an id that no longer resolves is
 * surfaced as unavailable rather than dropped, because dropping it is what
 * makes a record unsavable and un-fixable.
 */
export function resolveSelectedBusinessUnits(
  value: readonly string[],
  unitsById: ReadonlyMap<string, BusinessUnit>,
): SelectedBusinessUnit[] {
  return value.map((id) => {
    const unit = unitsById.get(id);
    if (!unit) {
      return { id, unit, archived: true, label: 'Unavailable unit', note: 'Unavailable' as const };
    }
    const archived = !isSelectableBusinessUnit(unit);
    return { id, unit, archived, label: unit.name, note: archived ? ('Archived' as const) : null };
  });
}

export interface BusinessUnitOptions {
  /** Selectable units — not archived, in catalog order. Gate rendering on this. */
  activeUnits: BusinessUnit[];
  /** EVERY unit by id, archived included — for resolving a stored selection. */
  unitsById: ReadonlyMap<string, BusinessUnit>;
  /** False until a unit list has actually arrived (loading or errored). */
  isLoaded: boolean;
  /** The fetch failed — `isLoaded` stays false, but waiting on it is pointless. */
  isError: boolean;
}

export function useBusinessUnitOptions(): BusinessUnitOptions {
  const { data, isError } = useBusinessUnits('all');
  const units = data ?? NO_UNITS;
  const activeUnits = useMemo(() => units.filter(isSelectableBusinessUnit), [units]);
  const unitsById = useMemo(() => {
    const map = new Map<string, BusinessUnit>();
    for (const unit of units) map.set(unit.id, unit);
    return map;
  }, [units]);
  return { activeUnits, unitsById, isLoaded: data !== undefined, isError };
}
